/* Church details — your answers to your church's own questions.
 *
 * Joining asks them, but not every door can: somebody an admin added, or who
 * accepted an invitation to a group, never saw the form. This is where they
 * answer, and where anybody corrects an answer later. The server runs the
 * same check joining does, required questions included.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { api, ApiError } from '@/api/client'
import {
  CustomFieldInputs,
  missingRequired,
} from '@/features/church/CustomFieldInputs'
import { MY_CHURCH_ANSWERS_KEY, type MyChurchAnswers } from './QuestionsNudge'


export function ChurchDetailsPage() {
  const qc = useQueryClient()
  const [data, setData] = useState<MyChurchAnswers | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .get<MyChurchAnswers>('/me/church-answers')
      .then((d) => {
        if (cancelled) return
        setData(d)
        setValues(d.answers)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(
          err instanceof ApiError ? err.message : 'Your details did not load.',
        )
      })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  async function save(e: FormEvent) {
    e.preventDefault()
    if (!data || busy) return
    const unanswered = missingRequired(data.fields, values)
    if (unanswered.length) {
      setError(`Please answer “${unanswered[0].label}”.`)
      return
    }
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const next = await api.put<MyChurchAnswers>('/me/church-answers', {
        field_values: values,
      })
      setData(next)
      setValues(next.answers)
      setSaved(true)
      // Saving is looking: the Home card goes once nothing required is left.
      qc.setQueryData(MY_CHURCH_ANSWERS_KEY, next)
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not save your details. Please try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  if (loadError || !data) {
    return (
      <Card className="p-s6 text-center">
        <p className="font-semibold">Your church details did not load.</p>
        <p className="mt-1 text-base text-ink-2">{loadError}</p>
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-lg font-extrabold tracking-tight">Church details</h1>
        <p className="text-base text-ink-2">
          What {data.church_name} asks of everyone who joins.
        </p>
      </header>

      {!data.fields.length ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          {data.church_name} has no questions for its members.
        </Card>
      ) : (
        <Card className="p-s4">
          <form onSubmit={save} className="space-y-s4">
            <CustomFieldInputs
              fields={data.fields}
              values={values}
              onChange={(v) => {
                setValues(v)
                setSaved(false)
              }}
              disabled={busy}
            />

            {error ? (
              <div
                role="alert"
                className="flex items-start gap-s2 rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>{error}</span>
              </div>
            ) : null}

            <div className="flex items-center justify-end gap-s3">
              {saved ? (
                <span className="flex items-center gap-1 text-base text-ink-2">
                  <CheckCircle2 className="h-4 w-4" aria-hidden />
                  Saved
                </span>
              ) : null}
              <Button type="submit" variant="primary" loading={busy}>
                Save
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}
